/// <amd-module name="Repos/Views/Settings/Repositories-Policies/Components/AutomaticReviewerPolicyPanel" />
import * as React from "react";
import { IAutomaticReviewerPolicy } from "Repos/Views/Settings/Repositories-Policies/Contracts/Contracts";
import * as Resources from "Repos/Views/Settings/Repositories-Policies/Resources";
import { IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Checkbox } from "VSSUI/Checkbox";
import { FormItem } from "VSSUI/FormItem";
import { Icon } from "VSSUI/Icon";
import { IdentityPicker, IIdentity } from "VSSUI/IdentityPicker";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner } from "VSSUI/Spinner";
import { TextField, TextFieldWidth } from "VSSUI/TextField";
import { Toggle } from "VSSUI/Toggle";
import { getSafeId } from "VSSUI/Util";
import { renderTooltipWithBreaks } from "./Util";

interface IAutomaticReviewerPolicyPanelProps {
    onDismiss: () => void;
    materializeIdentity: (identity: IIdentity) => Promise<IIdentity>;
    policy: IAutomaticReviewerPolicy;
    save: (policy: IAutomaticReviewerPolicy, handleError: (error: Error) => void) => void;
}

const requiredId = "required-automatic-reviewer";
const optionalId = "optional-automatic-reviewer";

export class AutomaticReviewerPolicyPanel extends VssComponent<IAutomaticReviewerPolicyPanelProps> {
    private creatorVoteCountsValue: IObservableValue<boolean>;
    private disabled: boolean;
    private formErrorMessage: IObservableValue<string | undefined>;
    private failureMessage: IObservableValue<string | undefined>;
    private reviewers: ObservableArray<IIdentity>;
    private includesGroup: IObservableValue<boolean>;
    private isApproverCountDisabled: IObservableValue<boolean>;
    private isBusy: IObservableValue<boolean>;
    private isToggled: IObservableValue<boolean>;
    private isValid: IObservableValue<boolean>;
    private messageValue: IObservableValue<string>;
    private approvalsRequiredFromEachGroup: IObservableValue<string>;
    private pathFilterValue: IObservableValue<string>;
    private peoplePickerProvider: PeoplePickerProvider;
    private requirementValue: IObservableValue<string>;
    private title: string;

    public constructor(props: IAutomaticReviewerPolicyPanelProps, context: IVssComponentContext) {
        super(props, context);
        this.isBusy = new ObservableValue<boolean>(false);
        this.peoplePickerProvider = new PeoplePickerProvider(context.pageContext, {
            identityTypes: ["user", "group"],
            options: {
                MinResults: 5,
                MaxResults: 20
            },
            operationScopes: ["ims", "source"]
        });

        const { policy } = this.props;
        if (policy.isEnterpriseManaged || policy.isInherited || policy.disabled) {
            this.title = Resources.ReviewerPolicy;
            this.disabled = true;
        } else {
            this.disabled = false;

            if (policy.id === -1) {
                this.title = Resources.AddNewReviewerPolicy;
            } else {
                this.title = Resources.EditReviewerPolicy;
            }
        }

        const settings = policy.complexSettings!;
        this.isApproverCountDisabled = new ObservableValue<boolean>(false);
        this.includesGroup = new ObservableValue<boolean>(false);
        this.creatorVoteCountsValue = new ObservableValue(settings.creatorVoteCounts);
        this.messageValue = new ObservableValue<string>(settings.message || "");
        this.approvalsRequiredFromEachGroup = new ObservableValue(settings.minimumApproverCount.toFixed());
        this.pathFilterValue = new ObservableValue<string>(settings.filenamePatterns || "");
        this.requirementValue = new ObservableValue(settings.required ? requiredId : optionalId);
        this.reviewers = new ObservableArray<IIdentity>(settings.reviewers.slice(0, settings.reviewers.length));
        this.isValid = new ObservableValue(this.checkValid());
        this.reviewers.subscribe(() => (this.isValid.value = this.checkValid()));

        this.failureMessage = new ObservableValue<string | undefined>(undefined);
        this.formErrorMessage = new ObservableValue<string | undefined>(undefined);

        this.isToggled = new ObservableValue<boolean>(policy.value);
        this.checkApproverCount();
    }

    public render = (): JSX.Element => {
        const { policy } = this.props;

        return (
            <Observer isBusy={this.isBusy} formErrorMessage={this.formErrorMessage} isValid={this.isValid}>
                {() => {
                    return (
                        <Panel
                            onDismiss={() => this.props.onDismiss()}
                            titleProps={{ text: this.title }}
                            footerButtonProps={[
                                {
                                    text: Resources.Save,
                                    primary: true,
                                    onClick: () => {
                                        this.isBusy.value = true;

                                        const materializedReviewers = this.reviewers.value.map(r => this.props.materializeIdentity(r));
                                        Promise.all(materializedReviewers).then(reviewers => {
                                            this.props.save(
                                                {
                                                    ...policy,
                                                    value: this.isToggled.value,
                                                    complexSettings: {
                                                        ...policy.complexSettings!,
                                                        creatorVoteCounts: this.creatorVoteCountsValue.value,
                                                        filenamePatterns: this.pathFilterValue.value,
                                                        message: this.messageValue.value,
                                                        minimumApproverCount:
                                                            this.requirementValue.value === optionalId
                                                                ? 1
                                                                : parseInt(this.approvalsRequiredFromEachGroup.value) || 1,
                                                        required: this.requirementValue.value === requiredId,
                                                        reviewerIds: reviewers.map(r => r.localId) as string[],
                                                        reviewers: reviewers
                                                    }
                                                },
                                                this.handleError
                                            );
                                        });
                                    },
                                    disabled: this.disabled || !!this.formErrorMessage.value || !this.isValid.value
                                },
                                {
                                    text: Resources.Cancel,
                                    onClick: () => this.props.onDismiss()
                                }
                            ]}
                            description={
                                policy.isEnterpriseManaged
                                    ? Resources.ReadOnlyTooltip
                                    : policy.isInherited
                                    ? Resources.InheritanceMessageTable
                                    : undefined
                            }
                            overlayContent={this.isBusy.value ? <Spinner /> : undefined}
                        >
                            <div className="flex-column flex-grow rhythm-vertical-16">
                                <Observer failureMessage={this.failureMessage}>
                                    {() => {
                                        return this.failureMessage.value ? (
                                            <MessageCard
                                                onDismiss={() => (this.failureMessage.value = undefined)}
                                                severity={MessageCardSeverity.Error}
                                                className="flex-noshrink"
                                            >
                                                <div className="flex-column font-size-ms">{this.failureMessage.value}</div>
                                            </MessageCard>
                                        ) : null;
                                    }}
                                </Observer>
                                {policy.id !== -1 && (
                                    <FormItem label={Resources.Enabled}>
                                        <Toggle
                                            offText={Resources.ToggleOff}
                                            onText={Resources.ToggleOn}
                                            checked={this.isToggled}
                                            onChange={(event, value) => (this.isToggled.value = value)}
                                            disabled={this.disabled}
                                        />
                                    </FormItem>
                                )}
                                <FormItem label={Resources.Reviewers}>
                                    <IdentityPicker
                                        onIdentityAdded={identity => this.reviewers.push(identity) && this.checkApproverCount()}
                                        onIdentityRemoved={identity =>
                                            this.reviewers.splice(this.reviewers.value.indexOf(identity), 1) && this.checkApproverCount()
                                        }
                                        onIdentitiesRemoved={identities =>
                                            this.reviewers.removeAll(identity => identities.indexOf(identity) !== -1) && this.checkApproverCount()
                                        }
                                        pickerProvider={this.peoplePickerProvider}
                                        selectedIdentities={this.reviewers}
                                    />
                                </FormItem>
                                <FormItem label={Resources.PolicyRequirement}>
                                    <RadioButtonGroup
                                        onSelect={id => {
                                            this.requirementValue.value = id;
                                            if (id === optionalId) {
                                                this.approvalsRequiredFromEachGroup.value = "1";
                                            }
                                        }}
                                        selectedButtonId={this.requirementValue}
                                        className="flex-start policy-reviewer-radio-button-group"
                                    >
                                        <RadioButton
                                            id={optionalId}
                                            ariaLabelledBy={optionalId + "-rblabel"}
                                            key={optionalId}
                                            className="margin-top-16"
                                            disabled={this.disabled}
                                        >
                                            <div
                                                className="policy-radio-button-custom-label bolt-radio-button-label"
                                                id={getSafeId(optionalId + "-rblabel")}
                                            >
                                                {Resources.Optional}
                                            </div>
                                        </RadioButton>
                                        <RadioButton
                                            id={requiredId}
                                            ariaLabelledBy={requiredId + "-rblabel"}
                                            key={requiredId}
                                            className="margin-top-16 flex-start policy-radio-button-custom-label"
                                            disabled={this.disabled}
                                        >
                                            <div
                                                className="policy-radio-button-custom-label bolt-radio-button-label"
                                                id={getSafeId(requiredId + "-rblabel")}
                                            >
                                                {Resources.Required}
                                                <Observer
                                                    includesGroup={this.includesGroup}
                                                    requirementsValue={this.requirementValue}
                                                    isApproverCountDisabled={this.isApproverCountDisabled}
                                                >
                                                    {() => {
                                                        return this.requirementValue.value === requiredId && this.includesGroup.value ? (
                                                            <div>
                                                                <div>
                                                                    <span>{Resources.MinReviewersCount}</span>
                                                                </div>
                                                                <div className="flex-row flex-center">
                                                                    <TextField
                                                                        className="icon-margin"
                                                                        value={this.approvalsRequiredFromEachGroup}
                                                                        onChange={(event, value) => {
                                                                            this.approvalsRequiredFromEachGroup.value = value;
                                                                            this.isValid.value = this.checkValid();
                                                                        }}
                                                                        disabled={this.disabled || this.isApproverCountDisabled.value}
                                                                    />
                                                                    <Icon
                                                                        ariaLabel={Resources.AutomaticReviewerPolicyMinimumApprovalCountInfo}
                                                                        iconName="Info"
                                                                        tooltipProps={{
                                                                            renderContent: () =>
                                                                                renderTooltipWithBreaks(
                                                                                    Resources.AutomaticReviewerPolicyMinimumApprovalCountInfo
                                                                                )
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <span className="secondary-text body-xs">
                                                                        {Resources.ASignleGroupIsRequiredToSetCount}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ) : null;
                                                    }}
                                                </Observer>
                                            </div>
                                        </RadioButton>
                                    </RadioButtonGroup>
                                </FormItem>
                                <FormItem label={Resources.ForPullRequestsAffectingTheseFolders}>
                                    <div className="flex-row flex-grow flex-center">
                                        <TextField
                                            className="icon-margin flex-grow"
                                            value={this.pathFilterValue}
                                            onChange={(event, value) => (this.pathFilterValue.value = value)}
                                            width={TextFieldWidth.auto}
                                            disabled={this.disabled}
                                            containerClassName="flex-grow"
                                            inputClassName="flex-grow"
                                        />
                                        <Icon
                                            ariaLabel={Resources.PathFilterInfo}
                                            iconName="Info"
                                            tooltipProps={{
                                                renderContent: () => renderTooltipWithBreaks(Resources.PathFilterInfo)
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <span className="secondary-text body-xs">{Resources.ForPullRequestsAffectingTheseFoldersLeaveBlank}</span>
                                    </div>
                                </FormItem>
                                <FormItem label={Resources.CompletionOptions}>
                                    <Checkbox
                                        checked={this.creatorVoteCountsValue}
                                        onChange={(event, value) => (this.creatorVoteCountsValue.value = value)}
                                        disabled={this.disabled}
                                        label={Resources.MinReviewersAllowOwnChangeApproval}
                                    />
                                </FormItem>
                                <FormItem label={Resources.ActivityFeedMessage}>
                                    <div className="flex-row flex-center">
                                        <TextField
                                            className="icon-margin flex-grow"
                                            value={this.messageValue}
                                            onChange={(event, value) => (this.messageValue.value = value)}
                                            width={TextFieldWidth.auto}
                                            disabled={this.disabled}
                                            containerClassName="flex-grow"
                                            inputClassName="flex-grow"
                                        />
                                        <Icon
                                            ariaLabel={Resources.ActivityFeedMessageInfo}
                                            iconName="Info"
                                            tooltipProps={{
                                                renderContent: () => renderTooltipWithBreaks(Resources.ActivityFeedMessageInfo)
                                            }}
                                        />
                                    </div>
                                </FormItem>
                            </div>
                        </Panel>
                    );
                }}
            </Observer>
        );
    };

    private canChangeMinimumApproval(): boolean {
        return this.reviewers.length === 0 || (this.reviewers.length === 1 && this.reviewers.value[0].entityType.trim().toLowerCase() === "group");
    }

    private checkApproverCount = () => {
        if (this.reviewers.length && this.reviewers.value.find(identity => identity.entityType.trim().toLowerCase() === "group")) {
            this.includesGroup.value = true;
        } else {
            this.includesGroup.value = false;
        }

        this.isApproverCountDisabled.value = !this.canChangeMinimumApproval();
    };

    private checkValid(): boolean {
        const minimum = parseInt(this.approvalsRequiredFromEachGroup.value);
        if (!this.canChangeMinimumApproval() && minimum !== 1) {
            // We better coerce this value when reviewers are updated, than show errors to users
            this.approvalsRequiredFromEachGroup.value = "1";
        }

        return this.reviewers.length > 0 && parseInt(this.approvalsRequiredFromEachGroup.value) > 0;
    }

    private handleError = (error: Error) => {
        this.isBusy.value = false;
        this.failureMessage.value = error.toString();
    };
}
